import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Store, MapPin, Phone, Mail, Edit, Eye, Building2, ChevronLeft, AlertCircle, WifiOff, CheckCircle2, Sparkles, ArrowRight, Activity, TrendingUp } from 'lucide-react'
import { useStore } from '../../../contexts/StoreContext'
import { api } from '../../../lib/api'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

const MyStores = () => {
  const navigate = useNavigate()
  const { activeStore, setActiveStore } = useStore()
  const [stores, setStores] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(true)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  useEffect(() => {
    loadStores()
  }, [])

  const loadStores = async () => {
    const cacheKey = 'my_stores_list'
    const cachedStores = getCacheStale<any[]>(cacheKey)
    if (cachedStores && cachedStores.length > 0) {
      setStores(cachedStores)
      setLoading(false)
      setIsOnline(true)
    }
    
    try {
      setLoading(true)
      setError(null)
      const response = await api.getMyStores()
      
      const allStores = (response.all || []).map((store: any) => {
        const isActiveValue = store.is_active !== undefined && store.is_active !== null 
          ? Boolean(store.is_active) 
          : (store.isActive !== undefined && store.isActive !== null ? Boolean(store.isActive) : false)
        
        return {
          ...store,
          isActive: isActiveValue,
          is_active: isActiveValue
        }
      })
      setStores(allStores)
      setIsOnline(true)
      setCache(cacheKey, allStores, 10 * 60 * 1000)

      const approvedStores = (response.approved || []).map((store: any) => {
        const isActiveValue = store.is_active !== undefined && store.is_active !== null 
          ? Boolean(store.is_active) 
          : (store.isActive !== undefined && store.isActive !== null ? Boolean(store.isActive) : false)
        
        return {
          ...store,
          isActive: isActiveValue,
          is_active: isActiveValue
        }
      })
      
      if (approvedStores.length > 0 && !activeStore) {
        const firstActive = approvedStores.find((s: any) => s.is_active || s.isActive) || approvedStores[0]
        setActiveStore(firstActive)
      }
    } catch (err: any) {
      console.error('Error loading stores:', err)
      
      if ((err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) && cachedStores) {
        showOfflineToast()
        setIsOnline(false)
        return
      }
      
      setError(err.response?.data?.message || 'Error al cargar los negocios')
      setIsOnline(false)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateStore = () => {
    navigate('/create-store')
  }

  const handleViewStore = (store: any) => {
    setActiveStore(store)
    navigate(`/store-owner/dashboard`)
  }

  const handleManageStore = (store: any) => {
    setActiveStore(store)
    navigate(`/store/${store.id}/products`)
  }

  const handleStoreSettings = (store: any) => {
    setActiveStore(store)
    navigate(`/store/${store.id}/settings`)
  }

  const handleSetActiveStore = async (store: any) => {
    try {
      const response = await api.updateMyStore(store.id, {
        is_active: true
      })

      if (response.success) {
        const updatedStoreData = response.data || store
        const updatedStore = { 
          ...updatedStoreData, 
          is_active: true, 
          isActive: true 
        }
        setActiveStore(updatedStore)
        
        setStores(prevStores => 
          prevStores.map(s => 
            s.id === store.id 
              ? { ...s, is_active: true, isActive: true }
              : s
          )
        )
        
        await loadStores()
        toast.success(`${store.name} activado exitosamente`)
      } else {
        toast.error('Error al activar el negocio')
      }
    } catch (error: any) {
      console.error('Error activating store:', error)
      toast.error(error.response?.data?.message || 'Error al activar el negocio')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-emerald-50/80 text-emerald-700 border-emerald-200/50'
      case 'pending': return 'bg-amber-50/80 text-amber-700 border-amber-200/50'
      case 'rejected': return 'bg-red-50/80 text-red-700 border-red-200/50'
      default: return 'bg-gray-50/80 text-gray-700 border-gray-200/50'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return 'Aprobado'
      case 'pending': return 'Pendiente'
      case 'rejected': return 'Rechazado'
      default: return status
    }
  }

  if (loading && stores.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando tus negocios...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 sm:py-8">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
            <button
              onClick={() => navigate(-1)}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
            >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
            </button>
              <div className="flex-1 text-center px-2 sm:px-4 min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1 sm:mb-2 truncate">
                Mis Negocios
              </h1>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">
                  {stores.length} negocio{stores.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={handleCreateStore}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl hover:shadow-2xl transition-all duration-200 active:scale-95 flex-shrink-0"
            >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="bg-amber-50/80 backdrop-blur-xl border border-amber-200/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg">
            <div className="flex items-center gap-3">
              {!isOnline ? (
                <WifiOff className="w-5 h-5 text-amber-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              )}
              <p className="text-sm text-amber-800 font-semibold">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        {stores.length === 0 ? (
          <div className="text-center py-16">
            {!isOnline ? (
              <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-8 sm:p-12 max-w-lg mx-auto">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100/80 rounded-xl sm:rounded-2xl mb-6">
                  <WifiOff className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Sin conexión</h3>
                <p className="text-gray-600 mb-8 font-medium">No se puede cargar la información. Verifica tu conexión a internet.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-2xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 active:scale-95 font-semibold shadow-lg hover:shadow-xl"
                >
                  Reintentar
                </button>
              </div>
            ) : (
              <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-8 sm:p-12 max-w-lg mx-auto">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl shadow-xl mb-8">
                  <Store className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">No tienes negocios registrados</h3>
                <p className="text-gray-600 mb-8 font-medium">Comienza creando tu primer negocio para empezar a vender</p>
                <button
                  onClick={handleCreateStore}
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl transition-all duration-200 active:scale-95 font-semibold shadow-xl hover:shadow-2xl"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crear Mi Primer Negocio
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 lg:space-y-6">
            {stores.map((store) => {
              const isActive = store.is_active !== undefined ? store.is_active : (store.isActive !== undefined ? store.isActive : false)
              const isCurrentActive = activeStore?.id === store.id
              
              return (
                <div
                  key={store.id}
                  className={`group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border overflow-hidden transition-all duration-500 hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 ${
                    isCurrentActive 
                      ? 'border-blue-300/50 bg-white/80' 
                      : 'border-white/20 hover:bg-white/80'
                  }`}
                >
                  <div className="p-4 sm:p-6 lg:p-10">
                    {/* Store Header */}
                    <div className="flex items-start justify-between mb-4 sm:mb-6">
                      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-br rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300 ${
                          isCurrentActive
                            ? 'from-blue-600 to-indigo-600'
                            : 'from-gray-500 to-gray-600'
                        }`}>
                          <Store className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-2 sm:mb-3 group-hover:text-blue-600 transition-colors truncate ${
                            isCurrentActive ? 'text-blue-700' : 'text-gray-900'
                          }`}>
                            {store.name}
                          </h3>
                          <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
                            <span className={`inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold border backdrop-blur-sm ${getStatusColor(store.status || 'pending')}`}>
                              {getStatusLabel(store.status || 'pending')}
                            </span>
                            <span className={`inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold border backdrop-blur-sm ${
                              isActive 
                                ? 'bg-emerald-50/80 text-emerald-700 border-emerald-200/50' 
                                : 'bg-red-50/80 text-red-700 border-red-200/50'
                            }`}>
                              <Activity className="h-3 w-3 mr-1 sm:mr-1.5" />
                              {isActive ? 'Activo' : 'Inactivo'}
                            </span>
                            {isCurrentActive && (
                              <span className="inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold bg-blue-50/80 text-blue-700 border border-blue-200/50 backdrop-blur-sm">
                                <CheckCircle2 className="h-3 w-3 mr-1 sm:mr-1.5" />
                                Actual
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Store Details - Compacto */}
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-gray-700 font-medium">{store.address}</span>
                      </div>
                      {store.phone && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-700 font-medium">{store.phone}</span>
                        </div>
                      )}
                      {store.email && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-700 font-medium truncate">{store.email}</span>
                        </div>
                      )}
                    </div>

                    {/* Stats - Compacto */}
                    {(store as any).status === 'approved' && (
                      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6 p-4 sm:p-6 bg-white/40 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl">
                        <div className="text-center">
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{store.product_count || 0}</p>
                          <p className="text-[10px] sm:text-xs text-gray-600 font-semibold mt-1">Productos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{store.order_count || 0}</p>
                          <p className="text-[10px] sm:text-xs text-gray-600 font-semibold mt-1">Pedidos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{store.today_orders || 0}</p>
                          <p className="text-[10px] sm:text-xs text-gray-600 font-semibold mt-1">Hoy</p>
                        </div>
                      </div>
                    )}

                    {/* Active Store Indicator - Compacto */}
                    {isCurrentActive && (
                      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 backdrop-blur-sm border border-blue-200/50 rounded-xl sm:rounded-2xl flex items-center gap-2 sm:gap-3 shadow-lg">
                        <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-bold text-blue-700">Negocio activo actualmente</span>
                      </div>
                    )}

                    {/* Action Buttons - Compacto */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                      {(!isActive || !isCurrentActive) && (
                        <button
                          onClick={() => handleSetActiveStore(store)}
                          className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl min-h-[70px] sm:min-h-[80px] ${
                            !isActive 
                              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700' 
                              : 'bg-white/60 backdrop-blur-sm border border-white/30 text-blue-700 hover:bg-white/80'
                          }`}
                        >
                          <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                          <span>{!isActive ? 'Activar' : 'Seleccionar'}</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleViewStore(store)}
                        className="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-white bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl min-h-[70px] sm:min-h-[80px]"
                      >
                        <Eye className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                        <span>Dashboard</span>
                      </button>
                      <button
                        onClick={() => handleManageStore(store)}
                        className="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl min-h-[70px] sm:min-h-[80px]"
                      >
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                        <span>Gestionar</span>
                      </button>
                      <button
                        onClick={() => handleStoreSettings(store)}
                        className="flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl min-h-[70px] sm:min-h-[80px]"
                      >
                        <Edit className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                        <span>Configurar</span>
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}

            {/* Add New Store Card - Compacto */}
              <button
                onClick={handleCreateStore}
              className="w-full bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border-2 border-dashed border-blue-300/50 p-6 sm:p-8 lg:p-10 text-center hover:border-blue-400/70 hover:bg-white/80 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <Plus className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Agregar Nuevo Negocio</h3>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600 mb-4 sm:mb-6 font-medium">Expande tu negocio con más ubicaciones</p>
              <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold shadow-lg group-hover:shadow-xl transition-all duration-300">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Crear Negocio
              </div>
              </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MyStores
